import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileSpreadsheet, PackagePlus, Search, Tag } from "lucide-react";
import { toast } from "sonner";
import { QuickAddDialog } from "@/components/QuickAddDialog";
import { EditProductDialog } from "@/components/EditProductDialog";
import { api, errMsg } from "@/lib/api";
import { fmtExpiry, formatMoney, toMinor } from "@/lib/money";
import { useOnline } from "@/lib/connectivity";
import { applyPending, kvGet, kvSet, productsKey } from "@/lib/localdb";
import { onSynced, useOutbox } from "@/lib/outbox";
import { NeedsInternetNote } from "@/components/Offline";
import { useAuth } from "@/context/AuthContext";

function BatchTable({ batches }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {batches.map((b) => (
        <span
          key={b.id}
          data-testid={`batch-${b.batch_no}`}
          className={`rounded-md px-2 py-1 font-mono text-[10.5px] ${b.expired ? "bg-rose-50 text-rose-700 line-through" : b.qty === 0 ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-800"}`}
        >
          {b.batch_no} · {fmtExpiry(b.expiry)} · qty <b data-testid={`batch-qty-${b.batch_no}`}>{b.qty}</b>{b.expired ? " · expired" : ""}
        </span>
      ))}
    </div>
  );
}

export default function Products() {
  const { session } = useAuth();
  const biz = session.business;
  const [raw, setRaw] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const nav = useNavigate();
  const online = useOnline();
  const outbox = useOutbox();
  const pharmacy = biz.business_type === "pharmacy";
  const cacheKey = productsKey(session);

  const load = useCallback(
    () =>
      api.get("/products")
        .then((r) => {
          setRaw(r.data);
          kvSet(cacheKey, r.data);
        })
        .catch(async (e) => {
          if (e.response) toast.error(errMsg(e));
          else setRaw((await kvGet(cacheKey)) || { products: [] });
        }),
    [cacheKey]
  );
  useEffect(() => {
    load();
    return onSynced(load);
  }, [load]);
  const data = useMemo(() => applyPending(raw, outbox.items, session.branch.id, pharmacy), [raw, outbox.items, session.branch.id, pharmacy]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.products || []).filter((p) => !s || p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s));
  }, [data, q]);

  return (
    <div className="h-full overflow-y-auto" data-testid="products-page">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Catalogue · {data?.products.length ?? "…"} items</div>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Products</h1>
            <p className="mt-1 text-sm text-slate-500">One catalogue for every branch. Prices and batches shown for {session.branch.name}.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!online && <NeedsInternetNote className="h-12" />}
            <Link to="/price-tags" aria-disabled={!online} onClick={(e) => !online && e.preventDefault()} data-testid="products-price-tags-link" className={`flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 font-semibold text-slate-800 hover:border-slate-900 ${online ? "" : "pointer-events-none opacity-40"}`}>
              <Tag className="h-4 w-4" /> Price tags
            </Link>
            <Link to="/import" aria-disabled={!online} onClick={(e) => !online && e.preventDefault()} data-testid="products-import-link" className={`flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 font-semibold text-slate-800 hover:border-slate-900 ${online ? "" : "pointer-events-none opacity-40"}`}>
              <FileSpreadsheet className="h-4 w-4" /> Import Excel
            </Link>
            <button onClick={() => setOpen(true)} disabled={!online} data-testid="products-quick-add-button" className="flex h-12 items-center gap-2 rounded-xl bg-amber-500 px-5 font-semibold text-slate-950 hover:bg-amber-400 disabled:bg-slate-200 disabled:text-slate-400">
              <PackagePlus className="h-4 w-4" /> Quick add
            </button>
          </div>
        </div>
        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or barcode" data-testid="products-search-input" className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 outline-none focus:border-slate-900 focus:ring-4 focus:ring-amber-200" />
        </div>
        <ul className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="products-list">
          {shown.map((p) => (
            <li key={p.id} className="px-4 py-3.5 sm:px-5" data-testid={`product-row-${p.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">
                    {p.name}
                    {p.negative_stock && <span className="rounded-full bg-rose-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-rose-700" data-testid={`product-negative-stock-${p.id}`}>Negative stock</span>}
                  </div>
                  <div className="font-mono text-[11px] text-slate-400">{p.barcode || "no barcode"} · {p.category} · tax {p.effective_tax_rate}%</div>
                </div>
                {!pharmacy && (
                  <div className="w-16 text-right" data-testid={`product-stock-${p.id}`}>
                    <div className="font-mono text-[9.5px] uppercase tracking-widest text-slate-400">Stock</div>
                    <div className={`money text-base font-bold ${p.stock == null ? "text-slate-300" : p.stock < 0 ? "text-rose-600" : "text-slate-900"}`}>{p.stock == null ? "—" : p.stock}</div>
                  </div>
                )}
                <div className="money text-right text-base font-bold text-slate-900" data-testid={`product-row-price-${p.id}`}>
                  <span className="mr-1 text-[10px] text-slate-400">{biz.currency}</span>{formatMoney(toMinor(p.price, biz.decimals), biz.decimals)}
                  {p.branch_price && <div className="text-[10px] font-semibold text-amber-700" data-testid={`product-row-branch-price-${p.id}`}>{session.branch.name} price · base {p.base_price}</div>}
                </div>
                <button onClick={() => nav(`/price-tags?ids=${p.id}`)} disabled={!online} data-testid={`product-tag-button-${p.id}`} className="grid h-10 w-10 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900 disabled:opacity-40" aria-label="Print tag" title={online ? "Print tag" : "Needs internet"}>
                  <Tag className="h-4 w-4" />
                </button>
                <button onClick={() => setEdit(p)} disabled={!online} title={online ? "" : "Needs internet"} data-testid={`product-edit-button-${p.id}`} className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:border-slate-900 disabled:opacity-40">Edit</button>
              </div>
              {p.batches && <BatchTable batches={p.batches} />}
            </li>
          ))}
        </ul>
      </div>
      <EditProductDialog product={edit} biz={biz} branch={session.branch} onClose={() => setEdit(null)} onSaved={load} />
      <QuickAddDialog open={open} onOpenChange={setOpen} biz={biz} onCreated={(p) => { toast.success(`${p.name} added`, { action: { label: "Print tag", onClick: () => nav(`/price-tags?ids=${p.id}`) } }); load(); }} />
    </div>
  );
}
